"use client";

import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";

import { fetchRain, joinRain, tipRain } from "@/app/api/rain/api";
import { useAuth } from "@/app/hooks/context/AuthContext";
import { getEcho } from "@/app/lib/echo";
import {
    RainJoinedEvent,
    RainOpeningEvent,
    RainPaidEvent,
    RainState,
    RainTippedEvent,
} from "@/types/rain";

/** Safety-net re-sync — broadcasts do the real work. */
const POLL_MS = 60_000;
/** How long the payout card stays up before the next countdown takes over. */
const RESULT_MS = 30_000;

export interface RainResult {
    pot: number;
    share: number;
    participantCount: number;
    /** What the logged-in viewer personally won, if anything. */
    myShare: number;
    voided: boolean;
}

/**
 * Chat rain: a soko-points pot that drops on a timer and is split between the
 * players who pressed JOIN during the last couple of minutes.
 *
 * The client is a projection of server state — GET /rain on mount, then the
 * public `rain` channel drives every transition (`.rain-opening`,
 * `.rain-joined`, `.rain-paid`). The countdown runs locally against a server
 * clock offset so a skewed client clock can't show the wrong time left.
 *
 * Lives in a provider rather than a plain hook because two places need the same
 * cycle: the banner inside the chat panel, and the floating chat button that is
 * on screen even when the panel is shut — without that second one, a player with
 * the chat closed would never see the two-minute join window at all. One
 * provider also means one socket subscription, so closing the panel can't tear
 * the button's listeners down with it.
 */
function useRainSource() {
    const { user, refreshUser, updateSokoBalance } = useAuth();
    const userId = user?.id ?? null;

    const [enabled, setEnabled] = useState(false);
    const [rain, setRain] = useState<RainState | null>(null);
    const [result, setResult] = useState<RainResult | null>(null);
    const [joining, setJoining] = useState(false);
    const [tipping, setTipping] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [secondsLeft, setSecondsLeft] = useState(0);
    // 0..1 through the cycle — the progress bar on the rain box.
    const [progress, setProgress] = useState(0);

    // serverNow - clientNow, so the countdown survives a wrong local clock.
    const clockOffset = useRef(0);
    const endsAt = useRef<number | null>(null);
    // The bar measures time until the payout on the cycle's own scale. It is NOT
    // driven by starts_at: that is the pot's accrual window, which is shorter
    // than a full interval on the first cycle after a restart, and would leave
    // the bar looking barely started with half the time gone.
    const intervalMs = useRef(60 * 60 * 1000);

    // AuthContext rebuilds refreshUser on every render; going through a ref
    // keeps it out of the socket effect's deps, which must not re-run (a
    // re-run tears the `rain` subscription down and back up).
    const refreshUserRef = useRef(refreshUser);
    refreshUserRef.current = refreshUser;

    const applyState = useCallback((next: RainState) => {
        clockOffset.current = new Date(next.server_time).getTime() - Date.now();
        endsAt.current = new Date(next.ends_at).getTime();
        intervalMs.current = Math.max(1, next.interval_minutes || 60) * 60_000;
        setRain(next);
    }, []);

    const sync = useCallback(async () => {
        try {
            const { enabled: on, rain: state } = await fetchRain();
            setEnabled(on);
            if (state) applyState(state);
        } catch {
            /* rain state unavailable; broadcasts will correct us */
        }
    }, [applyState]);

    // Re-sync when the viewer changes: `joined` and `eligibility` are per-user.
    useEffect(() => {
        sync();
    }, [sync, userId]);

    useEffect(() => {
        const interval = setInterval(sync, POLL_MS);
        return () => clearInterval(interval);
    }, [sync]);

    // Local countdown. Ticks every second off the corrected clock.
    useEffect(() => {
        const tick = () => {
            if (!endsAt.current) return;
            const now = Date.now() + clockOffset.current;
            const remaining = endsAt.current - now;

            setSecondsLeft(Math.max(0, Math.round(remaining / 1000)));
            setProgress(Math.min(1, Math.max(0, 1 - remaining / intervalMs.current)));
        };
        tick();
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [rain?.id, rain?.ends_at, rain?.interval_minutes]);

    useEffect(() => {
        const echo = getEcho();
        const channel = echo.channel("rain");

        channel.listen(".rain-opening", (e: RainOpeningEvent) => {
            setEnabled(true);
            setResult(null);
            setRain((prev) => {
                clockOffset.current = new Date(e.server_time).getTime() - Date.now();
                endsAt.current = new Date(e.ends_at).getTime();
                // Keep this viewer's own fields — the broadcast is the same for everyone.
                return prev
                    ? { ...prev, ...e, joined: prev.id === e.id ? prev.joined : false }
                    : null;
            });
            // A viewer who had no state yet (or a stale cycle) needs the per-user fields.
            sync();
        });

        channel.listen(".rain-joined", (e: RainJoinedEvent) => {
            setRain((prev) =>
                prev && prev.id === e.id
                    ? { ...prev, participant_count: e.participant_count, pot: e.pot }
                    : prev
            );
        });

        channel.listen(".rain-tipped", (e: RainTippedEvent) => {
            setRain((prev) =>
                prev && prev.id === e.id
                    ? { ...prev, pot: e.pot, tips_total: e.tips_total }
                    : prev
            );
        });

        channel.listen(".rain-paid", (e: RainPaidEvent) => {
            const mine = userId ? e.winners.find((w) => w.user_id === userId) : undefined;

            setResult({
                pot: e.pot,
                share: e.share,
                participantCount: e.participant_count,
                myShare: mine?.amount ?? 0,
                voided: e.status === "void",
            });

            // We just got paid — pull the new balance rather than guessing it.
            if (mine) refreshUserRef.current();

            setRain((prev) => {
                clockOffset.current = new Date(e.server_time).getTime() - Date.now();
                endsAt.current = new Date(e.next.ends_at).getTime();
                // Fresh cycle: this viewer has neither joined nor tipped it yet.
                return prev
                    ? { ...prev, ...e.next, joined: false, my_tip: 0 }
                    : prev;
            });

            // The broadcast carries the new cycle's headline numbers; pull the
            // rest (eligibility, carry-over breakdown) so the panel is exact.
            sync();

        });

        // Reconnect hardening: the socket can miss a whole cycle while it's down.
        const conn = (echo.connector as { pusher?: { connection: { bind: (e: string, cb: () => void) => void } } }).pusher?.connection;
        let firstConnect = true;
        conn?.bind("connected", () => {
            if (firstConnect) {
                firstConnect = false;
                return;
            }
            sync();
        });

        return () => {
            echo.leaveChannel("rain");
        };
    }, [sync, userId]);

    // The payout card hands the box back to the next cycle on its own. Kept out
    // of the socket effect so a re-subscribe can never cancel it and leave the
    // card up forever.
    useEffect(() => {
        if (!result) return;
        const timer = setTimeout(() => setResult(null), RESULT_MS);
        return () => clearTimeout(timer);
    }, [result]);

    const join = async () => {
        if (!rain || joining) return;
        setJoining(true);
        setError(null);
        try {
            applyState(await joinRain());
        } catch (e) {
            setError(e instanceof Error ? e.message : "Could not join the rain.");
        } finally {
            setJoining(false);
        }
    };

    /** Move `amount` of the viewer's own Soko Points into the pot. */
    const tip = async (amount: number) => {
        if (!rain || tipping) return false;
        setTipping(true);
        setError(null);
        try {
            const { rain: next, soko_points } = await tipRain(amount);
            applyState(next);
            updateSokoBalance(soko_points);
            // The "X tipped the rain" line comes back through the chat channel as
            // a real message — nothing to add locally.
            return true;
        } catch (e) {
            setError(e instanceof Error ? e.message : "Could not tip the rain.");
            return false;
        } finally {
            setTipping(false);
        }
    };

    return {
        enabled,
        rain,
        result,
        secondsLeft,
        progress,
        joining,
        tipping,
        error,
        loggedIn: !!user,
        join,
        tip,
        clearError: () => setError(null),
        dismissResult: () => setResult(null),
    };
}

type RainContextValue = ReturnType<typeof useRainSource>;

const RainContext = createContext<RainContextValue | null>(null);

export function RainProvider({ children }: { children: ReactNode }) {
    return <RainContext.Provider value={useRainSource()}>{children}</RainContext.Provider>;
}

export function useRain(): RainContextValue {
    const ctx = useContext(RainContext);
    if (!ctx) throw new Error("useRain() must be used inside <RainProvider>");
    return ctx;
}
